/**
 * Where to go after sign-in, kept beside the OIDC round trip (twin slice
 * 1, W4 step 3).
 *
 * WHY THE OIDC `state` IS NOT ENOUGH. `login()` hands the return path to
 * `signinRedirect({ state })`, and a clean callback gives it back as
 * `user.state`. But oidc-client-ts does not store `state` with the user
 * (`User.toStorageString` drops it), so the provider's other way out of
 * `completeSignin` -- the callback's one-time code already spent, the
 * user recovered from storage -- has no path at all, and a person who
 * opened a robot link signed-out landed on `/overview` instead. The stash
 * is that path's second copy.
 *
 * `sessionStorage`, not the session store's choice of `localStorage`: a
 * return path belongs to the tab that asked for it, and the OIDC redirect
 * comes back to that same tab. Another tab's sign-in must never inherit
 * it.
 *
 * Every access is guarded. Safari private mode and blocked site data
 * throw on `setItem`; a stash that cannot be written must cost the person
 * their return path, never their sign-in.
 *
 * The value is whatever `safeReturnTo` already passed. Readers still pass
 * it through `safeReturnTo` again before navigating: storage is not a
 * trust boundary this module can vouch for.
 */

const KEY = 'nexsoss:return-to-v1';

// Each function reaches `window.sessionStorage` INSIDE its own try: some
// browsers throw on the property access itself when storage is blocked,
// not only on the call.

export function stashReturnTo(path: string): void {
  try {
    window.sessionStorage.setItem(KEY, path);
  } catch {
    // Quota or private mode: the OIDC state still carries the path.
  }
}

export function readReturnToStash(): string | null {
  try {
    return window.sessionStorage.getItem(KEY) || null;
  } catch {
    return null;
  }
}

export function clearReturnToStash(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to clear if storage is unreachable.
  }
}

/**
 * Read the stash and clear it: a return path is for the one sign-in that
 * stashed it. Left behind, it outlived that sign-in -- every later
 * stored-user fallback in the tab returned it again. The read and the
 * clear are guarded apart, so a clear that fails still gives the path.
 */
export function takeReturnToStash(): string | null {
  const path = readReturnToStash();
  clearReturnToStash();
  return path;
}

export const RETURN_TO_STASH_KEY = KEY;
